import {Injectable} from '@angular/core';
import {ComponentStore} from '@ngrx/component-store';
import {ProductApiService} from '../../../../services/product/product-api.service';
import {Store} from '@ngrx/store';
import {SeFeToastService} from 'se-fe-toast';
import {
  selectHistory,
  selectQueryParams,
  selectSelectedOrganizationId,
  selectSelectedProductId,
} from '../../../../state/router/router.selectors';
import {EMPTY, exhaustMap, forkJoin, map, Observable, of, switchMap, tap, withLatestFrom} from 'rxjs';
import {tapResponse} from '@ngrx/operators';
import {
  applyPendingProductPriceFromSession,
  BundleSummary,
  CatalogSummary,
  getProductDetailDisplayPrice,
  mergeClientPriceIntoItem,
  mergeVariationsIfMissing,
  Product,
  ProductSpec,
  resolveDefaultItemVariationForDisplay,
  Store as ClareStore,
  variationIdsMatch,
} from '../../../../models/clare';
import {StoreApiService} from '../../../../services/store-api.service';
import {defaultProductQueryParams, ProductQueryParams, SpecsAndVariants} from '../../../../models';
import {SeFeModalComponent, SeFeModalFooterComponent} from 'se-fe-modal';
import {concatMap} from 'rxjs/operators';
import {UpdateSpecsAndVariantsService} from './update-specs-and-variants.service';
import {Image, SpecOption, Variation} from '@sportngin/se-commerce-sdk/models/clare';
import {BundleApiService} from '../../../../services/bundle/bundle-api.service';
import {VariationApiService} from '../../../../services/variation-api.service';
import {AnalyticsEventProperties, AnalyticsEvents, AnalyticsService} from '../../../../services/analytics.service';
import {CatalogApiService} from '../../../../services/catalog-api.service';
import {CatalogMembershipSyncService} from '../../../../services/catalog-membership-sync.service';

const PRODUCT_SAVE_ERROR = 'Sorry, but we could not save your product. Please try again later.';

export interface ProductImagesUpdate {
  images: Image[];
}

export interface ProductOrganizationUpdate {
  brand: string;
  taxCode: string;
  catalogIds: number[];
}

export interface ProductDetailComponentStore {
  vm$: Observable<ProductDetailVm>;
  error$: Observable<any>;
  loadProduct: () => void;
  loadOrganizationCatalogs: () => void;
  updateProductStatus: (active: boolean) => void;
  updateVariantStatus: (event: {id: string; active: boolean}) => void;
  updateVariantInventory: (variants: Variation[]) => void;
  updateSpecsAndVariants: (event: UpdateProductDetail<SpecsAndVariants>) => void;
  updateProductImageAndDescription: (event: UpdateProductDetail<ProductImagesUpdate>) => void;
  saveProductOrganization: (event: UpdateProductDetail<ProductOrganizationUpdate>) => void;
  updateProductAndPriceBreak: (event: UpdateProductDetail<{product: Product}>) => void;
  updateOnlySoldInBundles: (onlySoldInBundles: boolean) => void;
  setError: (error: string | undefined) => void;
}

const defaultImage = {
  src: '/assets/images/no-image-available.svg',
  alt: 'No Image Available',
};

export interface ItemDetailState {
  product: Product | undefined;
  variants: Variation[];
  specs: ProductSpec[];
  loadingArray: string[];
  store: ClareStore | undefined;
  error: string | undefined;
  statusUpdating?: boolean;
  onlySoldInBundlesUpdating?: boolean;
  bundles: BundleSummary[];
  catalogs: CatalogSummary[];
  catalogsLoading: boolean;
  organizationCatalogs: CatalogSummary[];
  organizationCatalogsLoading: boolean;
}

export interface ProductDetailVm {
  product: Product | undefined;
  variants: Variation[];
  specs: ProductSpec[];
  loading: boolean;
  image: {src: string; alt: string};
  images: Image[];
  error: string | undefined;
  backLink: {backLink: string; backLinkParams: Record<string, string>};
  allVariantsDisabled: boolean;
  price: number | undefined;
  vendorName: string;
  statusUpdating?: boolean;
  onlySoldInBundles: boolean;
  onlySoldInBundlesUpdating?: boolean;
  brandName: string | undefined;
  bundles: BundleSummary[];
  catalogs: CatalogSummary[];
  catalogsLoading: boolean;
  organizationCatalogs: CatalogSummary[];
  organizationCatalogsLoading: boolean;
  organizationId: string;
}

/**
 * Event interface for updating the product and related objects.
 */
export interface UpdateProductDetail<T> {
  /**
   * Objects being updated.
   */
  data: T;
  /**
   * Reference to the modal component from which the event was fired.
   */
  modal?: SeFeModalComponent;
  /**
   * Optional reference to the footer component from which the event was fired.
   */
  footer?: SeFeModalFooterComponent;
}

/**
 * Sorts variants by their spec options in a consistent order.
 * Groups by the first spec (lowest spec ID), then orders within each group
 * by subsequent spec options. Falls back to position when spec data is missing.
 * This ensures consistent display regardless of the order options were created.
 */
const sortItemVariationBySpecOptions = (a: Variation, b: Variation): number => {
  const aOptions = a.spec_options ?? [];
  const bOptions = b.spec_options ?? [];

  if (!aOptions.length || !bOptions.length) {
    return (a.position || 0) - (b.position || 0);
  }

  // Sort each variant's options by their parent spec ID for a stable comparison axis
  const aSorted = [...aOptions].sort(
    (x, y) => (getSpecOptionProductSpec(x)?.id || 0) - (getSpecOptionProductSpec(y)?.id || 0)
  );
  const bSorted = [...bOptions].sort(
    (x, y) => (getSpecOptionProductSpec(x)?.id || 0) - (getSpecOptionProductSpec(y)?.id || 0)
  );

  for (let i = 0; i < Math.min(aSorted.length, bSorted.length); i++) {
    const aOptId = aSorted[i].id || 0;
    const bOptId = bSorted[i].id || 0;
    if (aOptId !== bOptId) return aOptId - bOptId;
  }

  return (a.position || 0) - (b.position || 0);
};

function getSpecOptionProductSpec(option: SpecOption): ProductSpec | undefined {
  return option.product_spec;
}

@Injectable()
export class ProductDetailStore extends ComponentStore<ItemDetailState> implements ProductDetailComponentStore {
  //#region - ViewModel -
  readonly product$ = this.select(state => state.product);
  // readonly product$ = this.select(this.item$, item => {
  //   return item ? productToOcProduct(item) : undefined;
  // });
  readonly variants$ = this.select(state => state.variants?.sort(sortItemVariationBySpecOptions) || []);
  readonly specs$ = this.select(state => state.specs);
  readonly loading$ = this.select(state => state.loadingArray.length > 0);
  readonly image$ = this.select(this.product$, item => {
    if (item?.display_metadata?.images && item.display_metadata.images.length) {
      return item.display_metadata.images[0];
    } else {
      return defaultImage;
    }
  });
  readonly images$ = this.select(this.product$, item => {
    if (item?.display_metadata?.images && item.display_metadata.images.length) {
      return item.display_metadata.images;
    }
    return [defaultImage];
  });
  readonly vendorName$ = this.select(state => state.store?.name ?? '');
  readonly error$ = this.select(state => state.error);
  readonly itemUpdating$ = this.select(state => state.statusUpdating ?? false);
  readonly onlySoldInBundles$ = this.select(this.product$, item => item?.only_sold_in_bundles ?? false);
  readonly onlySoldInBundlesUpdating$ = this.select(state => state.onlySoldInBundlesUpdating ?? false);
  readonly brandName$ = this.select(this.product$, item => item?.display_metadata?.brand);
  readonly itemVariations$ = this.select(state => state.variants ?? []);
  readonly bundles$ = this.select(state => state.bundles);
  readonly catalogs$ = this.select(state => state.catalogs);
  readonly catalogsLoading$ = this.select(state => state.catalogsLoading);
  readonly organizationCatalogs$ = this.select(state => state.organizationCatalogs);
  readonly organizationCatalogsLoading$ = this.select(state => state.organizationCatalogsLoading);
  readonly organizationId$ = this.store
    .select(selectSelectedOrganizationId)
    .pipe(map(organizationId => organizationId ?? ''));

  readonly backLink$ = this.select(
    this.store.select(selectHistory).pipe(
      map(history => {
        // Find the last URL that is not a product or bundle detail page
        // This prevents navigation loops and ensures Back from bundle detail returns to list/create
        const isProductOrBundleDetail = (u: string) =>
          /\/products\/[^/?]+(?:\?.*)?$/.test(u) || /\/bundles\/(?!new(?:\?|$))[^/?]+(?:\?.*)?$/.test(u);
        for (let i = history.length - 1; i >= 0; i--) {
          const url = history[i];
          if (url && !isProductOrBundleDetail(url)) {
            return decodeURIComponent(url);
          }
        }
        return undefined;
      }),
      withLatestFrom(this.store.select(selectSelectedOrganizationId), this.store.select(selectQueryParams))
    ),
    ([prevUrl, orgId, currentQueryParams]) => {
      const baseLink = `/org/${orgId}/commerce/products`;
      const fallbackParams = this.extractQueryParams(currentQueryParams);

      // Default to product list if no previous URL, if it's undefined, or if running embedded in an iframe
      if (!prevUrl || prevUrl === 'undefined') {
        return {
          backLink: baseLink,
          backLinkParams: fallbackParams,
        };
      }

      try {
        // Handle both relative and absolute URLs
        let url: URL;
        if (prevUrl.startsWith('http')) {
          url = new URL(prevUrl);
        } else {
          url = new URL(prevUrl, window.location.origin);
        }

        const backLink = url.pathname;
        const allowedKeys = ['page', 'take', 'query', 'sortBy', 'direction'];
        const backLinkParams: Record<string, string> = {};
        url.searchParams.forEach((value, key) => {
          if (allowedKeys.includes(key)) {
            backLinkParams[key] = String(value);
          }
        });

        const sanitizedParams = Object.keys(backLinkParams).length ? backLinkParams : fallbackParams;

        return {backLink, backLinkParams: sanitizedParams};
      } catch (__error) {
        // Fallback if URL parsing fails
        return {
          backLink: baseLink,
          backLinkParams: fallbackParams,
        };
      }
    }
  );

  readonly allVariantsDisabled$ = this.select(
    this.variants$,
    (variants: Variation[]) => variants.length > 0 && variants.every(v => !v.active)
  );

  readonly price$ = this.select(state => getProductDetailDisplayPrice(state.product));

  readonly defaultItemVariation$ = this.select(state =>
    state.product ? resolveDefaultItemVariationForDisplay(state.product) : undefined
  );

  readonly vm$: Observable<ProductDetailVm> = this.select(
    this.product$,
    this.variants$,
    this.specs$,
    this.loading$,
    this.image$,
    this.images$,
    this.vendorName$,
    this.error$,
    this.backLink$,
    this.allVariantsDisabled$,
    this.price$,
    this.itemUpdating$,
    this.onlySoldInBundles$,
    this.onlySoldInBundlesUpdating$,
    this.brandName$,
    this.bundles$,
    this.catalogs$,
    this.catalogsLoading$,
    this.organizationCatalogs$,
    this.organizationCatalogsLoading$,
    this.organizationId$,
    (
      product,
      variants,
      specs,
      loading,
      image,
      images,
      vendorName,
      error,
      backLink,
      allVariantsDisabled,
      price,
      itemUpdating,
      onlySoldInBundles,
      onlySoldInBundlesUpdating,
      brandName,
      bundles,
      catalogs,
      catalogsLoading,
      organizationCatalogs,
      organizationCatalogsLoading,
      organizationId
    ): ProductDetailVm => ({
      product,
      variants,
      specs,
      loading,
      image,
      images,
      vendorName,
      error,
      backLink,
      allVariantsDisabled,
      price,
      statusUpdating: itemUpdating,
      onlySoldInBundles,
      onlySoldInBundlesUpdating,
      brandName,
      bundles,
      catalogs,
      catalogsLoading,
      organizationCatalogs,
      organizationCatalogsLoading,
      organizationId,
    })
  );
  //#endregion - ViewModel -

  readonly addLoading = this.updater((state, loadingThing: string) => {
    const loading = [...state.loadingArray, loadingThing];
    return {...state, loadingArray: loading};
  });

  readonly removeLoading = this.updater((state, loadingThing: string) => {
    const loading = state.loadingArray.filter(l => l !== loadingThing);
    return {...state, loadingArray: loading};
  });

  readonly setError = this.updater((state, error: string | undefined) => ({
    ...state,
    error,
  }));

  readonly updateVariantInventory = this.updater((state, variants: Variation[]) => ({
    ...state,
    variants,
  }));

  readonly init = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      tap(() => {
        this.loadProduct();
      })
    );
  });

  readonly loadProduct = this.effect<void>(trigger$ => {
    return trigger$.pipe(
      tap(() => this.addLoading('product')),
      withLatestFrom(this.store.select(selectSelectedProductId), this.store.select(selectSelectedOrganizationId)),
      switchMap(([, productId, organizationId]) =>
        this.productApi.getWithMergedVariations(productId!, organizationId!).pipe(
          tapResponse({
            next: item => {
              const itemWithPendingPrice = applyPendingProductPriceFromSession(item);
              this.patchState({product: itemWithPendingPrice});
              const variants =
                itemWithPendingPrice.variations?.filter(
                  v => !variationIdsMatch(v.id, itemWithPendingPrice.default_variation_id)
                ) || [];
              this.patchState({variants});
              this.loadProductBundles();
              this.loadProductCatalogs();
              this.loadStore();
              this.loadProductSpecs(String(itemWithPendingPrice.id));
            },
            error: () => {
              this.setError('An error occurred. Please refresh and try again');
            },
            finalize: () => {
              this.removeLoading('product');
            },
          })
        )
      )
    );
  });

  readonly loadProductBundles = this.effect<void>((trigger$: Observable<void>) => {
    return trigger$.pipe(
      withLatestFrom(this.store.select(selectSelectedProductId)),
      switchMap(([, productId]) => {
        this.addLoading('bundles');
        return this.bundleApi.getBundlesByProductId(String(productId)).pipe(
          tapResponse({
            next: (bundles: BundleSummary[]) => {
              this.patchState({bundles});
            },
            error: () => {
              this.setError('An error occurred. Please refresh and try again');
            },
            finalize: () => {
              this.removeLoading('bundles');
            },
          })
        );
      })
    );
  });

  readonly loadProductCatalogs = this.effect<void>((trigger$: Observable<void>) => {
    return trigger$.pipe(
      withLatestFrom(this.store.select(selectSelectedProductId), this.store.select(selectSelectedOrganizationId)),
      tap(() => this.patchState({catalogs: [], catalogsLoading: true})),
      switchMap(([, productId, organizationId]) =>
        this.catalogApi.getAllCatalogsByOrganizationId(organizationId!, {product_id: productId!}).pipe(
          tapResponse({
            next: catalogs => this.patchState({catalogs}),
            error: () => this.patchState({catalogs: []}),
            finalize: () => this.patchState({catalogsLoading: false}),
          })
        )
      )
    );
  });

  readonly loadOrganizationCatalogs = this.effect<void>((trigger$: Observable<void>) => {
    return trigger$.pipe(
      withLatestFrom(this.store.select(selectSelectedOrganizationId)),
      tap(() => this.patchState({organizationCatalogs: [], organizationCatalogsLoading: true})),
      switchMap(([, organizationId]) =>
        this.catalogApi.getAllCatalogsByOrganizationId(organizationId!).pipe(
          tapResponse({
            next: organizationCatalogs => this.patchState({organizationCatalogs}),
            error: () => this.patchState({organizationCatalogs: []}),
            finalize: () => this.patchState({organizationCatalogsLoading: false}),
          })
        )
      )
    );
  });

  readonly loadStore = this.effect<void>(trigger$ => {
    return trigger$.pipe(
      tap(() => this.addLoading('store')),
      withLatestFrom(this.store.select(selectSelectedOrganizationId)),
      switchMap(([, orgId]) =>
        this.storeApi.get(orgId!).pipe(
          tapResponse({
            next: store => {
              this.patchState({store});
            },
            error: () => {
              this.setError('An error occurred. Please refresh and try again');
            },
            finalize: () => {
              this.removeLoading('store');
            },
          })
        )
      )
    );
  });

  readonly loadProductSpecs = this.effect((itemId$: Observable<string>) => {
    return itemId$.pipe(
      tap(() => this.addLoading('specs')),
      switchMap(itemId =>
        this.productApi.getProductSpecs(itemId!).pipe(
          tapResponse({
            next: specs => this.patchState({specs}),
            error: () => this.setError('An error occurred. Please refresh and try again'),
            finalize: () => this.removeLoading('specs'),
          })
        )
      )
    );
  });

  readonly updateVariantStatus = this.effect((status$: Observable<{id: string; active: boolean}>) => {
    return status$.pipe(
      concatMap(({id, active}) => {
        return this.productApi.updateVariantStatus({id, active}).pipe(
          tapResponse({
            next: updatedVariant => {
              this.patchState(state => ({
                variants: state.variants.map(v => (v.id === updatedVariant.id ? updatedVariant : v)),
              }));
              this.variantStatusChanged();
            },
            error: () => {
              this.setError('An error occurred. Please refresh and try again');
            },
          })
        );
      })
    );
  });

  readonly variantStatusChanged = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      withLatestFrom(this.variants$),
      tap(([, variants]) => {
        const allVariantsInactive = variants.every(variant => !variant.active);
        if (allVariantsInactive) {
          this.updateProductStatus(false);
        }
      })
    );
  });

  readonly updateProductStatus = this.effect((status$: Observable<boolean>) => {
    return status$.pipe(
      withLatestFrom(this.product$, this.store.select(selectSelectedOrganizationId)),
      exhaustMap(([active, item, organizationId]) => {
        const dto: Partial<Product> = {
          id: item?.id,
          active,
        };
        this.patchState({statusUpdating: true});
        return this.productApi.update(dto, organizationId!).pipe(
          tapResponse({
            next: updatedItem => {
              this.patchState({product: updatedItem});
              this.toast.done('Product updated');
              this.trackProductStatusChange(item?.active, updatedItem);
            },
            error: () => {
              this.setError('An error occurred. Please refresh and try again');
            },
            finalize: () => {
              this.patchState({statusUpdating: false});
            },
          })
        );
      })
    );
  });

  readonly updateOnlySoldInBundles = this.effect((onlySoldInBundles$: Observable<boolean>) => {
    return onlySoldInBundles$.pipe(
      withLatestFrom(this.product$, this.store.select(selectSelectedOrganizationId)),
      exhaustMap(([onlySoldInBundles, item, organizationId]) => {
        if (!item?.id) {
          this.toast.critical(PRODUCT_SAVE_ERROR);
          return EMPTY;
        }

        const previousItem = item;
        this.patchState({
          product: {...item, only_sold_in_bundles: onlySoldInBundles},
          onlySoldInBundlesUpdating: true,
        });

        return this.productApi
          .update(
            {
              id: item.id,
              only_sold_in_bundles: onlySoldInBundles,
            },
            organizationId!
          )
          .pipe(
            tapResponse({
              next: updatedItem => {
                this.patchState({product: updatedItem});
              },
              error: () => {
                this.patchState({product: previousItem});
                this.toast.critical(PRODUCT_SAVE_ERROR);
              },
              finalize: () => {
                this.patchState({onlySoldInBundlesUpdating: false});
              },
            })
          );
      })
    );
  });

  readonly updateSpecsAndVariants = this.effect((event$: Observable<UpdateProductDetail<SpecsAndVariants>>) => {
    return event$.pipe(
      withLatestFrom(this.product$, this.itemVariations$),
      exhaustMap(([event, item, itemVariations]) => {
        return this.updateSpecsAndVariantsService
          .updateSpecsAndGenerateVariants(event.data, item!, itemVariations)
          .pipe(
            tapResponse({
              next: ([variants, specs]) => {
                const addedVariantCount = this.countNewVariants(itemVariations, variants);
                this.patchState({variants, specs});
                event.modal?.close();
                this.toast.done('Variants updated');
                if (addedVariantCount > 0) {
                  this.trackProductVariantAdd(item?.id, addedVariantCount);
                }
              },
              error: () => {
                this.setError('An error occurred. Please refresh and try again');
              },
              finalize: () => {
                event.modal?.cancelSpinner();
              },
            })
          );
      })
    );
  });

  readonly updateProductImageAndDescription = this.effect<UpdateProductDetail<ProductImagesUpdate>>(data$ => {
    return data$.pipe(
      withLatestFrom(this.product$, this.store.select(selectSelectedOrganizationId)),
      exhaustMap(([{data, modal}, item, organizationId]) => {
        const hasNewProductMedia = this.hasNewProductMedia(item?.display_metadata?.images, data.images ?? []);
        const dto: Partial<Product> = {
          id: item?.id,
          display_metadata: {
            images: data.images ?? [],
          },
        };
        return this.productApi.update(dto, organizationId!).pipe(
          tapResponse({
            next: updatedItem => {
              this.patchState({product: updatedItem});
              this.toast.done('Product updated');
              this.setError(undefined);
              if (hasNewProductMedia) {
                this.trackProductMedia(updatedItem.id);
              }
              modal?.close();
            },
            error: () => {
              this.setError('An error occurred. Please refresh and try again');
            },
            finalize: () => {
              modal?.cancelSpinner();
            },
          })
        );
      })
    );
  });

  readonly saveProductOrganization = this.effect(
    (updateProductDetail$: Observable<UpdateProductDetail<ProductOrganizationUpdate>>) => {
      return updateProductDetail$.pipe(
        withLatestFrom(
          this.product$,
          this.catalogs$,
          this.organizationCatalogs$,
          this.store.select(selectSelectedOrganizationId)
        ),
        exhaustMap(([{data, modal}, item, catalogs, organizationCatalogs, organizationId]) => {
          if (!item || item.id === undefined || item.id === null) {
            modal?.cancelSpinner();
            this.setError('An error occurred. Please refresh and try again');
            return EMPTY;
          }

          const productId = item.id;
          const brandChanged = data.brand !== (item.display_metadata?.brand ?? '');
          const taxCodeChanged = data.taxCode !== (item.tax_code ?? '');
          if (taxCodeChanged && (item.default_variation_id === undefined || item.default_variation_id === null)) {
            modal?.cancelSpinner();
            this.setError('Could not determine product variant to update tax code.');
            return EMPTY;
          }

          return forkJoin({
            organizationDetails: this.updateProductOrganizationDetails(
              item,
              data,
              brandChanged,
              taxCodeChanged,
              organizationId!
            ),
            catalogs: this.updateProductCatalogs(productId, data.catalogIds, catalogs, organizationCatalogs),
          }).pipe(
            tapResponse({
              next: ({organizationDetails, catalogs: catalogUpdate}) => {
                if (!brandChanged && !taxCodeChanged && !catalogUpdate.changed) {
                  this.setError(undefined);
                  modal?.close();
                  return;
                }

                this.patchState({
                  product: {...organizationDetails.product, tax_code: organizationDetails.taxCode},
                  catalogs: catalogUpdate.catalogs,
                });
                this.toast.done('Product updated');
                this.setError(undefined);
                modal?.close();
              },
              error: () => {
                this.setError('An error occurred. Please refresh and try again');
              },
              finalize: () => {
                modal?.cancelSpinner();
              },
            })
          );
        })
      );
    }
  );

  readonly updateProductAndPriceBreak = this.effect(
    (updateProductDetail$: Observable<UpdateProductDetail<{product: Product}>>) => {
      return updateProductDetail$.pipe(
        withLatestFrom(this.product$, this.store.select(selectSelectedOrganizationId)),
        exhaustMap(([event, item, organizationId]) => {
          const {product} = event.data;
          const defaultVariation = resolveDefaultItemVariationForDisplay(item!);
          const variationId = defaultVariation?.id ?? item?.default_variation_id;
          const priceCents = Number(product.price_cents);
          const variationIdNum = variationId !== undefined && variationId !== null ? Number(variationId) : Number.NaN;
          const canUpdate = item?.id !== undefined && item.id !== null && !Number.isNaN(variationIdNum);
          if (!canUpdate) {
            event.modal?.cancelSpinner();
            this.setError('Could not determine product variant to update price.');
            return EMPTY;
          }
          /**
           * Nested `variations` on `PATCH /finance/products/:id` are not persisted. Price is saved via
           * `PATCH …/product_variations/:id`. Variation is updated before the product so a product
           * metadata PATCH cannot overwrite the newly saved price on the server.
           */
          const priceDollars = priceCents / 100;
          return this.productApi
            .updateProductVariant({
              id: variationIdNum,
              price_cents: priceCents,
              price: priceDollars,
            })
            .pipe(
              switchMap(() =>
                this.productApi.update(
                  {
                    id: item.id,
                    name: product.name,
                    description: product.description,
                  },
                  organizationId!
                )
              ),
              switchMap((updatedItem: Product) =>
                mergeVariationsIfMissing(
                  updatedItem,
                  id => this.productApi.getProductVariants(id),
                  id => this.productApi.getProductVariant(id)
                ).pipe(map(merged => mergeClientPriceIntoItem(merged, variationId, priceCents)))
              ),
              tapResponse({
                next: (merged: Product) => {
                  this.patchState({product: merged});
                  this.toast.done('Product updated successfully');
                  event.modal?.close();
                },
                error: () => {
                  this.setError('An error occurred. Please refresh and try again');
                },
                finalize: () => {
                  event.modal?.cancelSpinner();
                },
              })
            );
        })
      );
    }
  );

  private updateProductOrganizationDetails(
    item: Product,
    data: ProductOrganizationUpdate,
    brandChanged: boolean,
    taxCodeChanged: boolean,
    organizationId: string
  ): Observable<{product: Product; taxCode: string | undefined}> {
    const productUpdate$ = brandChanged
      ? this.productApi.update(
          {
            id: item.id,
            display_metadata: {
              brand: data.brand,
            },
          },
          organizationId
        )
      : of(item);
    const taxCodeUpdate$ = taxCodeChanged
      ? this.variationApi
          .update({
            id: item.default_variation_id,
            tax_code: data.taxCode,
          })
          .pipe(map(variation => variation.tax_code ?? data.taxCode))
      : of(item.tax_code);

    return forkJoin({product: productUpdate$, taxCode: taxCodeUpdate$});
  }

  private updateProductCatalogs(
    productId: number,
    selectedCatalogIds: number[],
    catalogs: CatalogSummary[],
    organizationCatalogs: CatalogSummary[]
  ): Observable<{catalogs: CatalogSummary[]; changed: boolean}> {
    return this.catalogMembershipSync
      .sync({
        catalogable: {type: 'product', id: productId},
        currentCatalogIds: catalogs.map(catalog => catalog.id),
        selectedCatalogIds,
      })
      .pipe(
        map(result => {
          const catalogsById = new Map([...catalogs, ...organizationCatalogs].map(catalog => [catalog.id, catalog]));
          return {
            catalogs: result.selectedCatalogIds
              .map(catalogId => catalogsById.get(catalogId))
              .filter((catalog): catalog is CatalogSummary => catalog !== undefined),
            changed: result.catalogIdsToAssign.length > 0 || result.catalogIdsToRemove.length > 0,
          };
        })
      );
  }

  private trackProductStatusChange(previousActive: boolean | undefined, updated: Product): void {
    if (typeof updated.active !== 'boolean' || previousActive === updated.active) {
      return;
    }

    this.analytics.trackAction(
      updated.active ? AnalyticsEvents.ActivateProduct : AnalyticsEvents.DeactivateProduct,
      this.buildProductAnalyticsProperties(updated.id)
    );
  }

  private trackProductVariantAdd(productId: Product['id'] | undefined | null, variantCount: number): void {
    this.analytics.trackAction(
      AnalyticsEvents.AddProductVariant,
      this.buildProductAnalyticsProperties(productId, {
        variant_count: variantCount,
      })
    );
  }

  private trackProductMedia(productId: Product['id'] | undefined | null): void {
    this.analytics.trackAction(AnalyticsEvents.AddProductMedia, this.buildProductAnalyticsProperties(productId));
  }

  private buildProductAnalyticsProperties(
    productId: Product['id'] | undefined | null,
    properties: AnalyticsEventProperties = {}
  ): AnalyticsEventProperties {
    return {
      ...properties,
      ...(productId !== undefined && productId !== null ? {product_id: String(productId)} : {}),
      event_type: 1,
    };
  }

  private hasNewProductMedia(previousImages: Image[] | undefined, nextImages: Image[]): boolean {
    const previousSources = new Set((previousImages ?? []).map(image => image.src).filter(Boolean));
    return nextImages.some(image => !!image.src && !previousSources.has(image.src));
  }

  private countNewVariants(previousVariants: Variation[], nextVariants: Variation[]): number {
    const previousIds = new Set(previousVariants.map(variant => String(variant.id)).filter(Boolean));
    return nextVariants.filter(variant => !!variant.id && !previousIds.has(String(variant.id))).length;
  }

  private extractQueryParams(
    params: Partial<Record<keyof ProductQueryParams, unknown>> | undefined
  ): Record<string, string> {
    const defaults = defaultProductQueryParams();
    const merged = {
      page: defaults.page,
      take: defaults.take,
      ...params,
    };

    const allowedKeys: (keyof ProductQueryParams)[] = ['page', 'take', 'query', 'sortBy', 'direction'];
    return allowedKeys.reduce<Record<string, string>>((acc, key) => {
      const value = merged[key];
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = String(value);
      }
      return acc;
    }, {});
  }

  constructor(
    private productApi: ProductApiService,
    private storeApi: StoreApiService,
    private bundleApi: BundleApiService,
    private catalogApi: CatalogApiService,
    private catalogMembershipSync: CatalogMembershipSyncService,
    private variationApi: VariationApiService,
    private store: Store,
    private toast: SeFeToastService,
    private updateSpecsAndVariantsService: UpdateSpecsAndVariantsService,
    private analytics: AnalyticsService
  ) {
    super({
      product: undefined,
      variants: [],
      specs: [],
      loadingArray: [],
      store: undefined,
      error: undefined,
      bundles: [],
      catalogs: [],
      catalogsLoading: false,
      organizationCatalogs: [],
      organizationCatalogsLoading: false,
    });
  }
}
